import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { Text } from '@lusha-ui/text';
import { ArrowRight } from '@lusha/icons';
import { PLATFORM } from '@lusha/source-platforms';
import { CheckIcon, InfoDarkIcon } from '@lusha-ui/icons';
import { Label } from '@lusha-ui/label';
import { getTooltipProps, Tooltip } from '@lusha-ui/tooltip';
import { Button } from '@lusha-ui/button';
import { colors } from '@lusha-ui/theme';
import { Dropdown, DropdownItem, StyledDropdownItem, StyledDropdownInputText } from '@lusha-ui/dropdown';
import React, { useMemo } from 'react';
import { BILLING_CYCLES, DEFAULT_SEAT_AMOUNT } from '../constants';
import { useMST } from '@hooks/MSTHook';
import styled from '@emotion/styled';
import { useAnalytics } from '@lusha-engage/analytics';
const StyledDropdown = styled(Dropdown) `
  ${StyledDropdownItem},
  ${StyledDropdownInputText} {
    font-size: 13px !important;
  }
`;
const PlanCard = ({ billingCycle, selectedCredits, selectedSeats, onCreditsChange, onSeatsChange, handleClose }) => {
    const { pricing, userSettings } = useMST(store => ({
        pricing: store.pricing,
        userSettings: store.userSettings,
    }));
    const isMonthly = billingCycle === BILLING_CYCLES.MONTHLY;
    const analytics = useAnalytics();
    const seatInterval = isMonthly ? 'month' : 'year';
    const currentPrices = useMemo(() => {
        if (isMonthly) {
            return pricing.proMonthlyPrices;
        }
        return pricing.proAnnualPrices;
    }, [billingCycle, pricing.proMonthlyPrices, pricing.proAnnualPrices]);
    const seatProductsSteps = useMemo(() => {
        var _a;
        if (!pricing.proSeatProducts) {
            return [];
        }
        const seatProduct = pricing.proSeatProducts.products.find(p => p.code === 'SEATS_ONLY');
        if (!seatProduct) {
            return [];
        }
        const currentPrice = seatProduct.prices.find(({ interval }) => (interval === null || interval === void 0 ? void 0 : interval.unit) === seatInterval);
        return (_a = currentPrice.quantitySteps) !== null && _a !== void 0 ? _a : [];
    }, [pricing.proSeatProducts, billingCycle, seatInterval]);
    const totalPrice = useMemo(() => {
        const selectedPrice = currentPrices[selectedCredits];
        const selectedSeat = seatProductsSteps[selectedSeats];
        const totalSum = selectedSeat.finalPrice + selectedPrice.totalPrice;
        const rawPrice = isMonthly ? totalSum : totalSum / 12;
        const rounded = Math.ceil(rawPrice * 20) / 20;
        return rounded % 1 === 0 ? `$${rounded}` : `$${rounded.toFixed(2)}`;
    }, [
        selectedCredits,
        selectedSeats,
        billingCycle,
        pricing.proMonthlyPrices,
        pricing.proAnnualPrices,
        pricing.proSeatProducts,
    ]);
    function handleContinue() {
        var _a, _b, _c;
        const proPlan = pricing.proPlan;
        const selectedPrice = currentPrices[selectedCredits];
        const basePriceId = selectedPrice.id.replace(/-(?:Monthly|Yearly)-\d+$/, '');
        const seatProduct = (_b = (_a = pricing.proSeatProducts) === null || _a === void 0 ? void 0 : _a.products) === null || _b === void 0 ? void 0 : _b.find(p => p.code === 'SEATS_ONLY');
        const seatPrice = (_c = seatProduct === null || seatProduct === void 0 ? void 0 : seatProduct.prices) === null || _c === void 0 ? void 0 : _c.find(({ interval }) => (interval === null || interval === void 0 ? void 0 : interval.unit) === seatInterval);
        const params = new URLSearchParams({
            plan: proPlan.id,
            price: basePriceId,
            'product-price': selectedPrice.dynamicProductPriceId,
            quantity: String(selectedCredits),
            source: 'plugin_upgrade_popup',
            sub_platform: userSettings === null || userSettings === void 0 ? void 0 : userSettings.platform,
            platform: PLATFORM.PLUGIN,
            'extra-seat-product': seatProduct === null || seatProduct === void 0 ? void 0 : seatProduct.id,
            'extra-seat-price': seatPrice === null || seatPrice === void 0 ? void 0 : seatPrice.id,
            'seat-quantity': String(selectedSeats),
        });
        analytics.track({
            eventType: 'pricing_choose_plan_clicked',
            event_value: 'professional',
            event_additional_info: {
                plugin_click_source: pricing.openSource,
                billing_cycle: billingCycle,
                credits: selectedPrice === null || selectedPrice === void 0 ? void 0 : selectedPrice.totalCredits,
                seats: selectedSeats + DEFAULT_SEAT_AMOUNT,
            },
        });
        window.open(`${envConfig.BILLING_CHECKOUT_URL}/checkout/v3?${params.toString()}`, '_blank');
        handleClose();
    }
    const tooltipId = 'upgrade-popup-feature-tooltip';
    const basePlanLabel = (_jsx(Label, { css: css `
        margin-left: 4px;
      `, backgroundColor: "#dddddd", textColor: colors.black, text: "Base plan" }));
    return (_jsxs("div", { css: css `
        padding: 16px;
        border-radius: 16px;
        border: 1px solid ${colors.stroke.brand.DEFAULT};
        background: ${colors.background.surface.brand.accent['1'].DEFAULT};
        box-shadow: 0 8px 16px 0 rgba(21, 20, 23, 0.1);
      `, children: [_jsxs("div", { css: css `
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 4px;
        `, children: [_jsx(Text, { variant: "h4-s", children: "Pro" }), _jsxs(Text, { variant: "h3-s", children: [totalPrice, " /mo."] })] }), _jsx("div", { css: css `
          margin-bottom: 24px;
        `, children: _jsx(Text, { variant: "body-2-r", children: isMonthly ? 'Billed monthly' : 'Billed annually' }) }), _jsx("div", { css: css `
          margin-bottom: 8px;
        `, children: _jsx(StyledDropdown, { value: selectedCredits, onChange: onCreditsChange, size: "tiny", autoScrollToSelected: true, children: currentPrices === null || currentPrices === void 0 ? void 0 : currentPrices.map((price, index) => (_jsx(DropdownItem, { value: price.quantity, text: _jsxs(_Fragment, { children: [price.totalCredits, " Credits / ", isMonthly ? 'month' : 'year', ' ', index === 0 ? basePlanLabel : `(${price.discountPercent}% off)`] }), children: _jsxs(_Fragment, { children: [price.totalCredits, " Credits / ", isMonthly ? 'month' : 'year', ' ', index === 0 ? basePlanLabel : `(${price.discountPercent}% off)`] }) }, `price-${index}`))) }) }), _jsx("div", { css: css `
          margin-bottom: 16px;
        `, children: _jsx(StyledDropdown, { value: selectedSeats, onChange: onSeatsChange, size: "tiny", autoScrollToSelected: true, children: seatProductsSteps === null || seatProductsSteps === void 0 ? void 0 : seatProductsSteps.map((step, index) => (_jsx(DropdownItem, { value: step.quantity, text: _jsxs(_Fragment, { children: [step.quantity + DEFAULT_SEAT_AMOUNT, " seats included ", index === 0 ? basePlanLabel : ''] }), children: _jsxs(_Fragment, { children: [step.quantity + DEFAULT_SEAT_AMOUNT, " seats included ", index === 0 ? basePlanLabel : ''] }) }, `seat-step-${index}`))) }) }), _jsxs(Button, { css: css `
          width: 100%;
          margin-bottom: 8px;
        `, textVariant: "body-2-m", onClick: handleContinue, children: ["Continue to Pro ", _jsx(ArrowRight, {})] }), _jsx("div", { css: css `
          margin-bottom: 24px;
        `, children: _jsx(Text, { variant: "body-3-r", color: colors.text_and_icons.secondary.DEFAULT, children: "Credits granted upfront" }) }), _jsxs("div", { css: css `
          gap: 6px;
          display: flex;
          flex-direction: column;
        `, children: [_jsx(Text, { variant: "body-3-r", children: "Key features include:" }), _jsx("div", { css: css `
            gap: 6px;
            display: flex;
            flex-direction: column;
          `, children: pricing.proFeatures.map(({ feature, feature_description }, index) => {
                            return (_jsxs("div", { css: css `
                  display: flex;
                `, children: [_jsx(CheckIcon, { css: css `
                    margin-right: 8px;
                  `, color: colors.text_and_icons.positive.DEFAULT }), _jsx(Text, { variant: "body-3-r", color: colors.text_and_icons.secondary.DEFAULT, children: feature }), feature_description ? (_jsxs(_Fragment, { children: [_jsx(InfoDarkIcon, Object.assign({ css: css `
                        margin-left: 6px;
                      ` }, getTooltipProps({
                                                tooltipId: `${tooltipId}${index}`,
                                            }))), _jsx(Tooltip, { id: `${tooltipId}${index}`, children: _jsx("div", { css: css `
                          max-width: 200px;
                        `, children: feature_description }) })] })) : ('')] }, index));
                        }) })] })] }));
};
export default PlanCard;
